import React, { ReactNode } from "react";
import {
  SliceComponentType,
  SliceZoneProps,
  TODOSliceComponent,
} from "@prismicio/react";
import RichTextContent from "../rich-text-content/rich-text-content";
import {
  AdvancedTableSliceFragment,
  BenefitQuadrantSliceFragment,
  CapabilityListSliceFragment,
  CarbonOffsetSliceFragment,
  CenteredFullWidthCtaSliceFragment,
  ContentPostSliceFragment,
  CountUpSliceFragment,
  FeaturedCustomersSliceFragment,
  FullWidthCtaSliceFragment,
  ImageAndCaptionSliceFragment,
  ImageAndListSliceFragment,
  ImageCarouselSliceFragment,
  ImageGridSliceFragment,
  InlineCtaSliceFragment,
  LatestCapabilitiesSliceFragment,
  LinkGridSliceFragment,
  MediaMarqueeSliceFragment,
  NestedContentSliceFragment,
  NewsletterSignupSliceFragment,
  PullQuoteSliceFragment,
  QueriedResourcePostsSliceFragment,
  QuoteUploadComponentSliceFragment,
  ResourcesPostsSliceFragment,
  RichTextSliceFragment,
  SimpleInlineCtaSliceFragment,
  TableSliceFragment,
  TestimonialSliceFragment,
  TestimonialsSliceFragment,
  TextAndImageSliceFragment,
  TextAndModelFileSliceFragment,
  ThreeSimpleCardsSliceFragment,
  ThreeSimpleCirclesSliceFragment,
  XometryEverywhereSliceFragment,
  YoutubeVideoSliceFragment,
  TextAndImageRowsSliceFragment,
  VideoCtaSliceFragment,
  ImageCtaSliceFragment,
  AdvancedSliceSliceFragment,
  HeroAuthorSliceFragment,
  ContentCarouselSliceFragment,
} from "../../types/graphql";
import { IPageContext } from "../../types/types";
import ImageAndCaption from "../image-and-caption/image-and-caption";
import Testimonial from "../testimonial/testimonial";
import Testimonials from "../testimonials/testimonials";
import ThreeSimpleCards from "../three-simple-cards/three-simple-cards";
import FullWidthCTA from "../full-width-cta/full-width-cta";
import TextAndImage from "../text-and-image/text-and-image";
import NestedContent from "../nested-content/nested-content";
import Table from "../table/table";
import CapabilityList from "../capability-list/capability-list";
import ResourcesPosts from "../resources-posts/resources-posts";
import BenefitQuadrant from "../benefit-quadrant/benefit-quadrant";
import ListAndImage from "../list-and-image/list-and-image";
import ImageCarousel from "../image-carousel/image-carousel";
import FeaturedCustomers from "../featured-customers/featured-customers";
import NewsletterSignup from "../newsletter-signup/newsletter-signup";
import ThreeSimplecircles from "../three-simple-circles/three-simple-circles";
import FeaturedVideo from "../featured-video/featured-video";
import CenteredFullWidthCTA from "../centered-full-width-cta/centered-full-width-cta";
import LinkGrid from "../link-grid/link-grid";
import PostHighlight from "../post-highlight/post-highlight";
import ImageGrid from "../image-grid/image-grid";
import MediaMarquee from "../media-marquee/media-marquee";
import { AdvancedTable } from "../advanced-table/advanced-table";
import QueriedResourcesPosts from "../resources-posts/queried-resources-posts";
import SimpleInlineCTA from "../simple-inline-cta/simple-inline-cta";
import PullQuote from "../pull-quote/pull-quote";
import InlineCTA from "../inline-cta/inline-cta";
import XomEverywhere from "../xom-everywhere/xom-everywhere";
import { CarbonReporter } from "../carbon-reporter/carbon-reporter";
import CountUp from "../count-up/count-up";
import TextAndModelFile from "../text-and-model-file/text-and-model-file";
import QuoteUpload from "../quote-upload/quote-upload";
import LatestCapabilities from "../latest-capabilities/latest-capabilities";
import TextAndImageRows from "../text-and-image-rows/text-and-image-rows";
import VideoCta from "../video-cta/video-cta";
import ImageCta from "../image-cta/image-cta";
import AdvancedSlice from "../advanced-slice/advanced-slice";
import HeroAuthor from "../hero-author/hero-author";
import ContentCarousel from "../content-carousel/content-carousel";

// Enum key and value must match string slice_type value from prismic.
export enum SharedSliceType {
  rich_text = "rich_text",
  image_and_caption = "image_and_caption",
  testimonial = "testimonial",
  testimonials = "testimonials",
  three_simple_cards = "three_simple_cards",
  full_width_cta = "full_width_cta",
  text_and_image = "text_and_image",
  nested_content = "nested_content",
  table = "table",
  capability_list = "capability_list",
  resources_posts = "resources_posts",
  benefit_quadrant = "benefit_quadrant",
  "image-and-list" = "image-and-list",
  image_and_list = "image_and_list",
  image_carousel = "image_carousel",
  featured_customers = "featured_customers",
  newsletter_signup = "newsletter_signup",
  three_simple_circles = "three_simple_circles",
  youtube_video = "youtube_video",
  centered_full_width_cta = "centered_full_width_cta",
  link_grid = "link_grid",
  content_post = "content_post",
  image_grid = "image_grid",
  media_marquee = "media_marquee",
  advanced_table = "advanced_table",
  queried_resource_posts = "queried_resource_posts",
  simple_inline_cta = "simple_inline_cta",
  pull_quote = "pull_quote",
  inline_cta = "inline_cta",
  xometry_everywhere = "xometry_everywhere",
  carbon_offset = "carbon_offset",
  count_up = "count_up",
  text_and_model_file = "text_and_model_file",
  home_featured_customers = "home_featured_customers",
  quote_upload_component = "quote_upload_component",
  latest_capabilities = "latest_capabilities",
  text_and_image_rows = "text_and_image_rows",
  video_cta = "video_cta",
  image_cta = "image_cta",
  advanced_slice = "advanced_slice",
  hero_author = "hero_author",
  content_carousel = "content_carousel",
}

interface IWrapperProps {
  key: string;
  children: ReactNode;
}

export enum WrapperType {
  narrow = "narrow",
  fullWidth = "fullWidth",
}

const Slices: Record<SharedSliceType, WrappedSlice<IPageContext>> = {
  [SharedSliceType.rich_text]: {
    component: RichTextContent,
    wrapperType: WrapperType.fullWidth,
  },
  [SharedSliceType.home_featured_customers]: {
    component: FeaturedCustomers,
  },
  [SharedSliceType.image_and_caption]: {
    component: ImageAndCaption,
    wrapperType: WrapperType.narrow,
  },
  [SharedSliceType.testimonial]: {
    component: Testimonial,
    wrapperType: WrapperType.narrow,
  },
  [SharedSliceType.testimonials]: {
    component: Testimonials,
    wrapperType: WrapperType.fullWidth,
  },
  [SharedSliceType.three_simple_cards]: {
    component: ThreeSimpleCards,
    wrapperType: WrapperType.narrow,
  },
  [SharedSliceType.full_width_cta]: {
    component: FullWidthCTA,
  },
  [SharedSliceType.text_and_image]: {
    component: TextAndImage,
    wrapperType: WrapperType.narrow,
  },
  [SharedSliceType.nested_content]: {
    component: NestedContent,
    wrapperType: WrapperType.fullWidth,
  },
  [SharedSliceType.table]: {
    component: Table,
    wrapperType: WrapperType.narrow,
  },
  [SharedSliceType.capability_list]: {
    component: CapabilityList,
    wrapperType: WrapperType.fullWidth,
  },
  [SharedSliceType.resources_posts]: {
    component: ResourcesPosts,
    wrapperType: WrapperType.narrow,
  },
  [SharedSliceType.benefit_quadrant]: {
    component: BenefitQuadrant,
    wrapperType: WrapperType.narrow,
  },
  [SharedSliceType["image-and-list"]]: {
    component: ListAndImage,
    wrapperType: WrapperType.narrow,
  },
  [SharedSliceType.image_and_list]: {
    component: ListAndImage,
    wrapperType: WrapperType.narrow,
  },
  [SharedSliceType.image_carousel]: {
    component: ImageCarousel,
    wrapperType: WrapperType.narrow,
  },
  [SharedSliceType.featured_customers]: {
    component: FeaturedCustomers,
    wrapperType: WrapperType.narrow,
  },
  [SharedSliceType.newsletter_signup]: {
    component: NewsletterSignup,
  },
  [SharedSliceType.three_simple_circles]: {
    component: ThreeSimplecircles,
    wrapperType: WrapperType.narrow,
  },
  [SharedSliceType.youtube_video]: {
    component: FeaturedVideo,
    wrapperType: WrapperType.narrow,
  },
  [SharedSliceType.centered_full_width_cta]: {
    component: CenteredFullWidthCTA,
    wrapperType: WrapperType.fullWidth,
  },
  [SharedSliceType.link_grid]: {
    component: LinkGrid,
    wrapperType: WrapperType.narrow,
  },
  [SharedSliceType.content_post]: {
    component: PostHighlight,
    wrapperType: WrapperType.narrow,
  },
  [SharedSliceType.image_grid]: {
    component: ImageGrid,
    wrapperType: WrapperType.narrow,
  },
  [SharedSliceType.media_marquee]: {
    component: MediaMarquee,
    wrapperType: WrapperType.fullWidth,
  },
  [SharedSliceType.advanced_table]: {
    component: AdvancedTable,
    wrapperType: WrapperType.narrow,
  },
  [SharedSliceType.queried_resource_posts]: {
    component: QueriedResourcesPosts,
    wrapperType: WrapperType.fullWidth,
  },
  [SharedSliceType.simple_inline_cta]: {
    component: SimpleInlineCTA,
    wrapperType: WrapperType.narrow,
  },
  [SharedSliceType.pull_quote]: {
    component: PullQuote,
  },
  [SharedSliceType.inline_cta]: {
    component: InlineCTA,
    wrapperType: WrapperType.narrow,
  },
  [SharedSliceType.xometry_everywhere]: {
    component: XomEverywhere,
    wrapperType: WrapperType.narrow,
  },
  [SharedSliceType.carbon_offset]: {
    component: CarbonReporter,
    wrapperType: WrapperType.narrow,
  },
  [SharedSliceType.count_up]: {
    component: CountUp,
    wrapperType: WrapperType.fullWidth,
  },
  [SharedSliceType.text_and_model_file]: {
    component: TextAndModelFile,
    wrapperType: WrapperType.narrow,
  },
  [SharedSliceType.quote_upload_component]: {
    component: QuoteUpload,
  },
  [SharedSliceType.latest_capabilities]: {
    component: LatestCapabilities,
    wrapperType: WrapperType.fullWidth,
  },
  [SharedSliceType.text_and_image_rows]: {
    component: TextAndImageRows,
    wrapperType: WrapperType.fullWidth,
  },
  [SharedSliceType.video_cta]: {
    component: VideoCta,
    wrapperType: WrapperType.fullWidth,
  },
  [SharedSliceType.image_cta]: {
    component: ImageCta,
    wrapperType: WrapperType.fullWidth,
  },
  [SharedSliceType.advanced_slice]: {
    component: AdvancedSlice,
    wrapperType: WrapperType.fullWidth,
  },
  [SharedSliceType.hero_author]: {
    component: HeroAuthor,
    wrapperType: WrapperType.fullWidth,
  },
  [SharedSliceType.content_carousel]: {
    component: ContentCarousel,
    wrapperType: WrapperType.fullWidth,
  },
};

type SliceWrappers = Record<
  WrapperType,
  (props: IWrapperProps) => JSX.Element | null
>;

type WrappedSlice<TContext> = {
  component: SliceComponentType<any, TContext>;
  wrapperType?: WrapperType;
};

export type SharedSliceFragment = { type: SharedSliceType } & (
  | RichTextSliceFragment
  | AdvancedTableSliceFragment
  | BenefitQuadrantSliceFragment
  | CapabilityListSliceFragment
  | CarbonOffsetSliceFragment
  | FullWidthCtaSliceFragment
  | YoutubeVideoSliceFragment
  | FeaturedCustomersSliceFragment
  | CountUpSliceFragment
  | CenteredFullWidthCtaSliceFragment
  | PullQuoteSliceFragment
  | ContentPostSliceFragment
  | NewsletterSignupSliceFragment
  | NestedContentSliceFragment
  | ImageAndListSliceFragment
  | MediaMarqueeSliceFragment
  | ImageCarouselSliceFragment
  | LinkGridSliceFragment
  | InlineCtaSliceFragment
  | ImageGridSliceFragment
  | XometryEverywhereSliceFragment
  | ThreeSimpleCirclesSliceFragment
  | ThreeSimpleCardsSliceFragment
  | TextAndImageSliceFragment
  | TestimonialSliceFragment
  | TestimonialsSliceFragment
  | TableSliceFragment
  | SimpleInlineCtaSliceFragment
  | ResourcesPostsSliceFragment
  | QueriedResourcePostsSliceFragment
  | ImageAndCaptionSliceFragment
  | TextAndModelFileSliceFragment
  | QuoteUploadComponentSliceFragment
  | LatestCapabilitiesSliceFragment
  | TextAndImageRowsSliceFragment
  | VideoCtaSliceFragment
  | ImageCtaSliceFragment
  | AdvancedSliceSliceFragment
  | HeroAuthorSliceFragment
  | ContentCarouselSliceFragment
);

interface Slice<TContext> {
  slice: SharedSliceFragment;
  Wrappers: SliceWrappers;
  index: number;
  context: TContext;
  slice_type: SharedSliceType;
}

export const SharedSlice = (props: Slice<IPageContext>) => {
  const { Wrappers, slice, index, context } = props;
  return (
    <SharedSlices<IPageContext>
      slices={[slice]}
      Wrappers={Wrappers}
      components={Slices}
      index={index}
      key={index}
      context={context}
    />
  );
};

type SharedSlicesProps<TContext> = Omit<
  SliceZoneProps<TContext>,
  "components"
> & {
  Wrappers: SliceWrappers;
  index: number;
  components: Record<string, WrappedSlice<TContext>>;
};

export const SharedSlices = <TContext,>({
  slices = [],
  components,
  defaultComponent = TODOSliceComponent,
  context = {} as TContext,
  Wrappers,
  index,
}: SharedSlicesProps<TContext>): JSX.Element => {
  const renderedSlices = React.useMemo(() => {
    return slices.map((slice) => {
      const type = "slice_type" in slice ? slice.slice_type : slice.type;
      const component = components[type];
      if (!component) {
        throw new Error(`Unsupported shared slice ${type}`);
      }

      const key = `${index}-${JSON.stringify(slice)}`;

      const Wrapper =
        (component.wrapperType && Wrappers[component.wrapperType]) ||
        ((props: IWrapperProps) => <>{props.children}</>);
      const Slice = component.component;
      return (
        <Wrapper key={key}>
          <Slice
            slice={slice}
            index={index}
            slices={slices}
            context={context}
          />
        </Wrapper>
      );
    });
  }, [components, context, defaultComponent, slices]);

  return <>{renderedSlices}</>;
};
